/**
 * keybinds.ts — the keyboard's binding table, the same shape the pad's is.
 *
 * Every consumer used to test `e.code === 'KeyW'` in its own module, which is
 * why the keyboard was the one input a player could not change. The codes now
 * live in ONE table and every read goes through it, so a rebind reaches the
 * avatar, the skimmer, the ship and the sector at once.
 *
 * The rules are deliberately the pad's rules (`lib/gamepad.ts`), because a
 * player should not have to learn two:
 *   · a binding is a SET of codes — any of them satisfies the action;
 *   · a code may mean one thing per CONTEXT, and contexts that never share a
 *     frame may share a code (WASD drives on foot AND in a skimmer because you
 *     are only ever doing one of them);
 *   · a rebind that collides is refused and NAMES the clash rather than
 *     silently winning.
 *
 * One thing differs: an action may be left UNBOUND here. Guard and the attack
 * are the mouse's job by default, and forcing a key onto them would be a lie.
 *
 * `KeyboardEvent.code` is the unit, never `key`: `code` is the physical key, so
 * a binding survives a layout change and an AZERTY player's ZQSD lands where
 * their fingers already are.
 */

export type KeyActionName =
  // on foot
  | 'moveForward' | 'moveBack' | 'moveLeft' | 'moveRight'
  | 'jump' | 'sprint' | 'crouch' | 'attack' | 'guard' | 'interact'
  | 'weaponNext' | 'weaponPrev'
  // sweep 2026-10-05: toggle a held walk forward (any movement key, a swim, a mount or a cinematic ends it)
  | 'autoRun'
  // a skimmer
  | 'throttleUp' | 'throttleDown' | 'steerLeft' | 'steerRight' | 'handbrake' | 'boost'
  // a ship, in atmosphere or the sector
  | 'rollLeft' | 'rollRight' | 'levelOut' | 'fire'
  // the shell, wherever you are
  | 'menu' | 'map' | 'inventory'
  // voice rooms (2026-09-24): hold to talk when the room is on push-to-talk
  | 'voicePtt'
  // THE SECTOR (space campaign B, 2026-09-30): every key SpaceModule used to test as a hard-coded `e.code` is an action
  // now — so it rebinds, the Controls menu lists it, the HUD prints its CURRENT key, and the validator below can prove
  // no key means two things at once (R used to fire a missile AND level the wings; E rolled AND took an escort; G
  // repaired AND opened the chart; Tab cycled targets AND toggled the world).
  | 'spaceLand' | 'spaceTarget' | 'spaceMatchSpeed' | 'spaceNav' | 'spaceHyper' | 'spaceGalaxy' | 'spaceSystemMap'
  | 'spaceRepair' | 'spaceDock' | 'spaceBoard' | 'spaceDeck' | 'spaceTurret' | 'spaceMissile' | 'spaceCountermeasures'
  | 'spaceSubTarget' | 'spaceDroid' | 'spaceCockpit' | 'spaceScan' | 'spaceOps' | 'spaceEscort' | 'spaceBeam'
  | 'spacePowerWeapons' | 'spacePowerShields' | 'spacePowerEngines' | 'spacePowerBalanced' | 'spaceFurnish'
  // space campaign F: the survey desk window (sites, tenders, the expedition log, intel licences, the mystery)
  | 'spaceSurvey'
  // space campaign I: swap the fire group (what the primary `fire` and the secondary `spaceMissile` triggers fire)
  | 'spaceFireGroup'
  // space campaign G: the engineering panel (subsystems, crew, power, patches) and the cockpit's field-patch shortcut
  | 'spaceEngineering' | 'spacePatch';

/** The defaults — exactly what the code tested before the table existed. */
export const KEY_DEFAULT_BINDINGS: Readonly<Record<KeyActionName, readonly string[]>> = {
  moveForward: ['KeyW', 'ArrowUp'],
  moveBack:    ['KeyS', 'ArrowDown'],
  moveLeft:    ['KeyA', 'ArrowLeft'],
  moveRight:   ['KeyD', 'ArrowRight'],
  jump:        ['Space'],
  sprint:      ['ShiftLeft', 'ShiftRight'],
  crouch:      ['ControlLeft'],
  attack:      ['KeyF'],
  guard:       [],                       // the right mouse button, by default
  interact:    ['KeyE'],
  weaponNext:  ['KeyQ'],
  weaponPrev:  [],                       // Shift+Q, and the wheel
  // Free in the on-foot frame: not a letter any foot handler reads, not a KEY_FIXED shell key; `=` is Edit Mode's palette key
  // but Edit Mode is never on foot, and `=` is the sector's engineering key in ANOTHER frame. Numpad + for a full keyboard.
  autoRun:     ['Equal', 'NumpadAdd'],
  throttleUp:  ['KeyW', 'ArrowUp'],
  throttleDown:['KeyS', 'ArrowDown'],
  steerLeft:   ['KeyA', 'ArrowLeft'],
  steerRight:  ['KeyD', 'ArrowRight'],
  handbrake:   ['Space'],
  boost:       ['ShiftLeft', 'ShiftRight'],
  rollLeft:    ['KeyA', 'KeyQ'],
  rollRight:   ['KeyD', 'KeyE'],
  levelOut:    ['KeyR'],
  fire:        ['Space'],
  menu:        ['Escape'],
  map:         ['Tab'],
  inventory:   ['F1'],
  voicePtt:    ['CapsLock'],             // every letter already means something; rebindable
  // ── The sector. Every default is the key the sector always used EXCEPT the four that meant two things at once
  //    (space campaign B): the missile left R (R stays level-out, which atmospheric flight shares — nothing there
  //    moves), the escort left E (E stays roll right), the station repair left G (G stays the galaxy chart, now also
  //    beside a station) and the turret seat left Shift+B (a two-key chord — XAG 107 asks for a single press). ──
  spaceLand:            ['KeyX'],
  spaceTarget:          ['Tab'],
  spaceMatchSpeed:      ['KeyF'],
  spaceNav:             ['KeyN'],
  spaceHyper:           ['KeyJ'],
  spaceGalaxy:          ['KeyG'],
  spaceSystemMap:       ['KeyM'],
  spaceRepair:          ['Digit5'],
  spaceDock:            ['KeyP'],
  spaceBoard:           ['KeyB'],
  spaceDeck:            ['KeyI'],
  spaceTurret:          ['Digit6'],
  spaceMissile:         ['KeyZ'],
  spaceCountermeasures: ['KeyC'],
  spaceSubTarget:       ['KeyT'],
  spaceDroid:           ['KeyV'],
  spaceCockpit:         ['KeyY'],
  spaceScan:            ['KeyO'],
  spaceOps:             ['KeyK'],
  spaceEscort:          ['KeyL'],
  spaceBeam:            ['KeyU'],
  spacePowerWeapons:    ['Digit1'],
  spacePowerShields:    ['Digit2'],
  spacePowerEngines:    ['Digit3'],
  spacePowerBalanced:   ['Digit4'],
  spaceFurnish:         ['KeyE'],
  spaceSurvey:          ['Digit0'],     // space campaign F: every letter is taken in the sector; 7 / 8 / 9 are the harnesses' free rebind keys
  spaceFireGroup:       ['Minus'],      // space campaign I: the key after 0 (letters, 1–6 and 0 are live; 7 / 8 / 9 stay the harnesses' free keys)
  spaceEngineering:     ['Equal'],      // space campaign G: the engineering panel — the key after − (letters, digits and − are live)
  spacePatch:           ['Quote'],      // space campaign G: the cockpit's field patch (the worst group; again cancels) — ] / [ / \ are the sky preview's on a local origin
};

export const KEY_ACTIONS = Object.keys(KEY_DEFAULT_BINDINGS) as KeyActionName[];

/** The sector's actions, in the order the Controls menu lists them. */
export const SPACE_ACTIONS: readonly KeyActionName[] = [
  'spaceTarget', 'spaceMissile', 'spaceFireGroup', 'spaceCountermeasures', 'spaceSubTarget', 'spaceMatchSpeed',
  'spacePowerWeapons', 'spacePowerShields', 'spacePowerEngines', 'spacePowerBalanced',
  'spaceNav', 'spaceHyper', 'spaceSystemMap', 'spaceGalaxy', 'spaceLand', 'spaceDock', 'spaceBoard', 'spaceDeck',
  'spaceTurret', 'spaceRepair', 'spaceDroid', 'spaceBeam', 'spaceScan', 'spaceEscort', 'spaceOps', 'spaceCockpit',
  'spaceSurvey', 'spaceEngineering', 'spacePatch',
];

/** Which actions belong together. A CONTEXT is a list of actions; a FRAME (below) is the set of contexts that are live
 *  at the same moment. An action may sit in several contexts (the walk keys drive a station's promenade too). */
export const KEY_CONTEXTS: Readonly<Record<string, readonly KeyActionName[]>> = {
  foot: ['moveForward', 'moveBack', 'moveLeft', 'moveRight', 'jump', 'sprint', 'crouch', 'attack', 'guard', 'interact', 'weaponNext', 'weaponPrev', 'autoRun', 'menu', 'map', 'inventory', 'voicePtt'],
  vehicle: ['throttleUp', 'throttleDown', 'steerLeft', 'steerRight', 'handbrake', 'boost', 'menu', 'map', 'inventory', 'voicePtt'],
  // The stick — atmospheric ship flight AND the sector share it. `map` (Tab: the Commons ↔ personal-world toggle) is
  // NOT here any more: it lives in `world`, which is live in the air but not in the sector, where Tab cycles targets.
  flight: ['throttleUp', 'throttleDown', 'rollLeft', 'rollRight', 'levelOut', 'fire', 'boost', 'menu', 'inventory', 'voicePtt'],
  world: ['map'],
  // The sector in flight, live WITH the stick.
  space: [...SPACE_ACTIONS],
  // Walking a station's promenade or the Sovereign deck (the ship is parked; the stick is not live).
  spaceWalk: ['moveForward', 'moveBack', 'moveLeft', 'moveRight', 'spaceFurnish', 'spaceBoard', 'spaceDeck', 'spaceDock', 'spaceOps', 'menu', 'inventory', 'voicePtt'],
  // The dock window is up (the server says DOCKED): only the dock, the ops panel and the shell.
  docked: ['spaceDock', 'spaceOps', 'spaceSurvey', 'menu', 'inventory', 'voicePtt'],
};

/** The FRAMES — context sets that are live together (the report's rule: "a context set that can be live together
 *  counts as one"). A code may mean one action per frame; frames never share a moment, so they may reuse a code. */
export const KEY_FRAMES: Readonly<Record<string, readonly string[]>> = {
  onFoot: ['foot'],
  riding: ['vehicle'],
  skyFlight: ['flight', 'world'],
  sector: ['flight', 'space'],
  sectorWalk: ['spaceWalk'],
  sectorDocked: ['docked'],
};
export type KeyFrameName = 'onFoot' | 'riding' | 'skyFlight' | 'sector' | 'sectorWalk' | 'sectorDocked';

/** Keys that are NOT in the table but ARE live in every frame (hard-wired shell windows). A rebind onto one is refused
 *  and names it, exactly like a clash with an action. */
export const KEY_FIXED: ReadonlyArray<{ code: string; what: string }> = [
  { code: 'KeyH', what: 'the manual (Help)' },
  { code: 'Backquote', what: 'the debug panel' },
  { code: 'Enter', what: 'chat' },
  { code: 'F2', what: 'Edit Mode' }, { code: 'F3', what: 'the performance HUD' }, { code: 'F4', what: 'the Versona Lab' },
  { code: 'F6', what: 'the Workbench' }, { code: 'F7', what: 'uploads' }, { code: 'F8', what: 'My Worlds' },
  { code: 'F9', what: 'the Merchant Hive' }, { code: 'F10', what: 'the Crafting Guide' },
];

/** A readable name for a `KeyboardEvent.code`. */
export function keyLabel(code: string): string {
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  if (code.startsWith('Arrow')) return { Up: '↑', Down: '↓', Left: '←', Right: '→' }[code.slice(5)] ?? code;
  if (code.startsWith('Numpad')) return `Num ${code.slice(6)}`;
  const named: Record<string, string> = {
    Space: 'Space', Escape: 'Esc', Tab: 'Tab', Enter: 'Enter', Backspace: '⌫',
    ShiftLeft: 'L Shift', ShiftRight: 'R Shift', ControlLeft: 'L Ctrl', ControlRight: 'R Ctrl',
    AltLeft: 'L Alt', AltRight: 'R Alt', CapsLock: 'Caps', Backquote: '`', Minus: '−', Equal: '=',
    BracketLeft: '[', BracketRight: ']', Backslash: '\\', Semicolon: ';', Quote: "'",
    Comma: ',', Period: '.', Slash: '/',
  };
  return named[code] ?? code;
}

const LS = 'atlas:keybinds';

interface KeyService {
  binds: Record<KeyActionName, string[]>;
  capture: KeyActionName | null;
  /** The last capture's outcome. `fixed` names a hard-wired shell key the capture refused (see KEY_FIXED). */
  last: { action: KeyActionName; code: string | null; conflicts: KeyActionName[]; fixed?: string | null } | null;
  seq: number;
}

function loadBinds(): Record<KeyActionName, string[]> {
  const out = {} as Record<KeyActionName, string[]>;
  for (const a of KEY_ACTIONS) out[a] = [...KEY_DEFAULT_BINDINGS[a]];
  try {
    const raw = localStorage.getItem(LS);
    if (!raw) return out;
    const saved = JSON.parse(raw) as Record<string, string[]>;
    for (const [k, v] of Object.entries(saved)) {
      // An entry naming an action this build no longer has is DROPPED, so an old
      // save can never leave the keyboard half-wired.
      if (!(k in out) || !Array.isArray(v)) continue;
      out[k as KeyActionName] = v.filter((c) => typeof c === 'string' && c.length > 0 && c.length < 24);
    }
  } catch { /* private mode, or a corrupt save — the defaults stand */ }
  return out;
}

const G = globalThis as unknown as { __atlasKeys?: KeyService };
function svc(): KeyService {
  if (!G.__atlasKeys) G.__atlasKeys = { binds: loadBinds(), capture: null, last: null, seq: 0 };
  return G.__atlasKeys;
}

/** The live table (a copy — callers must not mutate the service's own). */
export function keyBindings(): Record<KeyActionName, string[]> {
  const s = svc();
  const out = {} as Record<KeyActionName, string[]>;
  for (const a of KEY_ACTIONS) out[a] = [...s.binds[a]];
  return out;
}

export function keyContextsOf(action: KeyActionName): string[] {
  return Object.entries(KEY_CONTEXTS).filter(([, list]) => list.includes(action)).map(([k]) => k);
}

/** The frames in which `action` is live. */
export function keyFramesOf(action: KeyActionName): KeyFrameName[] {
  const ctxs = new Set(keyContextsOf(action));
  return (Object.entries(KEY_FRAMES) as Array<[KeyFrameName, readonly string[]]>)
    .filter(([, list]) => list.some((c) => ctxs.has(c))).map(([f]) => f);
}

/** Every action live in `frame` (each once). */
export function keyActionsInFrame(frame: KeyFrameName): KeyActionName[] {
  const out: KeyActionName[] = [];
  for (const c of KEY_FRAMES[frame] ?? []) for (const a of KEY_CONTEXTS[c] ?? []) if (!out.includes(a)) out.push(a);
  return out;
}

/** The actions `code` triggers in `frame` under `table` (the live table by default). A valid table yields ≤ 1. */
export function keyActionsFor(code: string, frame: KeyFrameName, table: Readonly<Record<KeyActionName, readonly string[]>> = svc().binds): KeyActionName[] {
  return keyActionsInFrame(frame).filter((a) => table[a]?.includes(code));
}

/** Which actions binding `codes` to `action` would collide with — two actions clash when some FRAME holds both
 *  (the report's rule: contexts that are live together count as one). Empty = legal. */
export function keyConflicts(
  table: Readonly<Record<KeyActionName, readonly string[]>>,
  action: KeyActionName,
  codes: readonly string[],
): KeyActionName[] {
  const mine = new Set(codes);
  const frames = new Set(keyFramesOf(action));
  const out: KeyActionName[] = [];
  for (const other of KEY_ACTIONS) {
    if (other === action) continue;
    if (!keyFramesOf(other).some((f) => frames.has(f))) continue;
    if (table[other].some((c) => mine.has(c)) && !out.includes(other)) out.push(other);
  }
  return out;
}

/** The hard-wired shell key among `codes`, if any (KEY_FIXED is live in every frame). */
export function keyFixedClash(codes: readonly string[]): { code: string; what: string } | null {
  for (const c of codes) { const f = KEY_FIXED.find((k) => k.code === c); if (f) return f; }
  return null;
}

/** One clash in a table: in `frame`, `code` means every action in `actions` (and/or a fixed shell key). */
export interface KeyClash { frame: KeyFrameName; code: string; actions: KeyActionName[]; fixed: string | null }

/**
 * THE VALIDATOR: every code that means two things in one frame. The defaults must yield none; a player's saved table
 * may (a save made before new actions existed) — that is REPORTED, never silently re-bound.
 */
export function validateKeyTable(table: Readonly<Record<KeyActionName, readonly string[]>>): KeyClash[] {
  const out: KeyClash[] = [];
  for (const frame of Object.keys(KEY_FRAMES) as KeyFrameName[]) {
    const byCode = new Map<string, KeyActionName[]>();
    for (const a of keyActionsInFrame(frame)) {
      for (const c of table[a] ?? []) {
        const list = byCode.get(c) ?? [];
        if (!list.includes(a)) list.push(a);
        byCode.set(c, list);
      }
    }
    for (const [code, actions] of byCode) {
      const fixed = KEY_FIXED.find((k) => k.code === code)?.what ?? null;
      if (actions.length > 1 || fixed) out.push({ frame, code, actions, fixed });
    }
  }
  return out;
}

/** The live (saved) table's clashes — what the Controls menu reports. */
export function keySavedConflicts(): KeyClash[] { return validateKeyTable(svc().binds); }

/** Rebind. Returns the conflicts on refusal; an EMPTY binding is allowed here
 *  (the mouse covers guard and the attack). A hard-wired shell key (KEY_FIXED) is refused too — the capture records
 *  which in `keyLastCapture().fixed`. */
export function setKeyBinding(action: KeyActionName, codes: readonly string[]): KeyActionName[] {
  const s = svc();
  const clean = codes.filter((c) => typeof c === 'string' && c.length > 0);
  const clash = keyConflicts(s.binds, action, clean);
  if (clash.length) return clash;
  if (keyFixedClash(clean)) return [action]; // refused: the fixed key is named by the caller (keyFixedClash)
  s.binds[action] = [...clean];
  save();
  return [];
}

export function resetKeyBindings(): void {
  const s = svc();
  for (const a of KEY_ACTIONS) s.binds[a] = [...KEY_DEFAULT_BINDINGS[a]];
  try { localStorage.removeItem(LS); } catch { /* private mode */ }
}

function save(): void {
  const s = svc();
  const diff: Record<string, string[]> = {};
  for (const a of KEY_ACTIONS) {
    const d = KEY_DEFAULT_BINDINGS[a];
    if (s.binds[a].length !== d.length || s.binds[a].some((v, i) => v !== d[i])) diff[a] = s.binds[a];
  }
  try {
    if (Object.keys(diff).length) localStorage.setItem(LS, JSON.stringify(diff));
    else localStorage.removeItem(LS);
  } catch { /* private mode */ }
}

/** Is `code` bound to `action`? The one question every consumer asks. */
export function keyIs(code: string, action: KeyActionName): boolean {
  return svc().binds[action].includes(code);
}

/** Is any code bound to `action` currently held, in the caller's own key set? */
export function keyHeldIn(held: ReadonlySet<string>, action: KeyActionName): boolean {
  for (const c of svc().binds[action]) if (held.has(c)) return true;
  return false;
}

/** The readable binding, for a settings row. */
export function keyBindingText(action: KeyActionName): string {
  const b = svc().binds[action];
  return b.length ? b.map(keyLabel).join(' / ') : '—';
}

/** The CURRENT key for a HUD hint or a toast: the first binding, or "unbound" (report §22.1 — a hint never prints a
 *  literal the player may have changed). */
export function keyHint(action: KeyActionName): string {
  const b = svc().binds[action];
  return b.length ? keyLabel(b[0]) : 'unbound';
}

// ── Capture ──────────────────────────────────────────────────────────────────
// A rebind waits for the next key. It runs in the key handler rather than a poll
// because that is where the browser tells us the code.

export function keyBeginCapture(action: KeyActionName): void { svc().capture = action; }
export function keyCancelCapture(): void { svc().capture = null; }
export function keyCaptureActive(): KeyActionName | null { return svc().capture; }
export function keyCaptureSeq(): number { return svc().seq; }
export function keyLastCapture(): KeyService['last'] { return svc().last; }

/**
 * Offer a pressed code to a pending capture. Returns true when it consumed the
 * press, which is what stops the game also acting on it.
 *
 * Escape CANCELS rather than binds — it is the one key a player always needs to
 * get out with, and a capture that swallowed it could strand them in the prompt.
 */
export function keyCaptureStep(code: string): boolean {
  const s = svc();
  if (!s.capture) return false;
  const action = s.capture;
  if (code === 'Escape') { s.capture = null; s.last = { action, code: null, conflicts: [], fixed: null }; s.seq++; return true; }
  const fixed = keyFixedClash([code]);
  const conflicts = fixed ? [] : setKeyBinding(action, [code]);
  s.capture = null;
  s.last = { action, code: conflicts.length || fixed ? null : code, conflicts, fixed: fixed ? fixed.what : null };
  s.seq++;
  return true;
}
